// src/components/DepartmentCheckboxes.tsx
import React, { useMemo, useState } from 'react';

const FRENCH_DEPARTMENTS: string[] = [
  "01 - Ain","02 - Aisne","03 - Allier","04 - Alpes-de-Haute-Provence","05 - Hautes-Alpes",
  "06 - Alpes-Maritimes","07 - Ardèche","08 - Ardennes","09 - Ariège","10 - Aube",
  "11 - Aude","12 - Aveyron","13 - Bouches-du-Rhône","14 - Calvados","15 - Cantal",
  "16 - Charente","17 - Charente-Maritime","18 - Cher","19 - Corrèze","2A - Corse-du-Sud",
  "2B - Haute-Corse","21 - Côte-d'Or","22 - Côtes-d'Armor","23 - Creuse","24 - Dordogne",
  "25 - Doubs","26 - Drôme","27 - Eure","28 - Eure-et-Loir","29 - Finistère",
  "30 - Gard","31 - Haute-Garonne","32 - Gers","33 - Gironde","34 - Hérault",
  "35 - Ille-et-Vilaine","36 - Indre","37 - Indre-et-Loire","38 - Isère","39 - Jura",
  "40 - Landes","41 - Loir-et-Cher","42 - Loire","43 - Haute-Loire","44 - Loire-Atlantique",
  "45 - Loiret","46 - Lot","47 - Lot-et-Garonne","48 - Lozère","49 - Maine-et-Loire",
  "50 - Manche","51 - Marne","52 - Haute-Marne","53 - Mayenne","54 - Meurthe-et-Moselle",
  "55 - Meuse","56 - Morbihan","57 - Moselle","58 - Nièvre","59 - Nord",
  "60 - Oise","61 - Orne","62 - Pas-de-Calais","63 - Puy-de-Dôme","64 - Pyrénées-Atlantiques",
  "65 - Hautes-Pyrénées","66 - Pyrénées-Orientales","67 - Bas-Rhin","68 - Haut-Rhin","69 - Rhône",
  "70 - Haute-Saône","71 - Saône-et-Loire","72 - Sarthe","73 - Savoie","74 - Haute-Savoie",
  "75 - Paris","76 - Seine-Maritime","77 - Seine-et-Marne","78 - Yvelines","79 - Deux-Sèvres",
  "80 - Somme","81 - Tarn","82 - Tarn-et-Garonne","83 - Var","84 - Vaucluse",
  "85 - Vendée","86 - Vienne","87 - Haute-Vienne","88 - Vosges","89 - Yonne",
  "90 - Territoire de Belfort","91 - Essonne","92 - Hauts-de-Seine","93 - Seine-Saint-Denis",
  "94 - Val-de-Marne","95 - Val-d'Oise","971 - Guadeloupe","972 - Martinique","973 - Guyane",
  "974 - La Réunion","976 - Mayotte"
];

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  required?: boolean;
};

export default function DepartmentCheckboxes({ value, onChange, required }: Props) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => FRENCH_DEPARTMENTS.filter(d =>
      d.toLowerCase().includes(query.toLowerCase())
    ),
    [query]
  );

  const allSelected = filtered.length > 0 && filtered.every(d => value.includes(d));

  const toggleOne = (dep: string) => {
    if (value.includes(dep)) onChange(value.filter(v => v !== dep));
    else onChange([...value, dep]);
  };

  const toggleAllFiltered = () => {
    if (allSelected) {
      onChange(value.filter(v => !filtered.includes(v)));
    } else {
      const union = new Set([...value, ...filtered]);
      onChange(Array.from(union));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Rechercher un département…"
          className="w-full border rounded-lg px-3 py-2"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Recherche département"
        />
        <button
          type="button"
          className="border rounded-lg px-3 py-2"
          onClick={toggleAllFiltered}
        >
          {allSelected ? 'Tout désélectionner (filtré)' : 'Tout sélectionner (filtré)'}
        </button>
      </div>

      {required && value.length === 0 && (
        <p className="text-xs text-red-600">Au moins un département est requis</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-auto border rounded-lg p-3">
        {filtered.map(dep => (
          <label key={dep} className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value.includes(dep)}
              onChange={() => toggleOne(dep)}
            />
            <span>{dep}</span>
          </label>
        ))}
        {filtered.length === 0 && (
          <span className="text-sm text-gray-500">Aucun département…</span>
        )}
      </div>
    </div>
  );
}
